// Обучение новичка «за руку»: на каждом шаге подсвечивается одна кнопка (остальной экран затемнён
// и не нажимается) и рядом — одна короткая фраза, что сделать. Шаг определяется по серверному шагу
// обучения и по тому, что сейчас на экране: открыто окно стройки — подсвечиваем «Построить», нет —
// нужный участок или вкладку меню. Пропустить можно только осознанно — с подтверждением.
import { post } from '@/api/client';
import type { CastleView } from '@/api/types';
import { reloadCastle } from '@/lib/hooks';
import { useGame } from '@/store/game';
import { cx } from '@/ui';
import { defaultBalance } from '@sk/shared';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useHasCity3D } from '@/town/City3D';

const TOTAL = defaultBalance.quests.list.filter((q) => q.type === 'tutorial').length;
/** Высота руки-указателя вместе с отступом. */
const HAND_SIZE = 44;

interface Hint {
  /** data-tut элемента, который подсвечиваем; без него — просто подсказка сверху. */
  tut?: string;
  text: string;
  /** Затемнить всё вокруг и не давать нажимать мимо. */
  dim?: boolean;
  /** Подсказка-ожидание (идёт стройка, найм, бой): чаще обновлять состояние. */
  wait?: boolean;
}

function building(castle: CastleView, type: string) {
  return castle.buildings.find((b) => b.type === type);
}

/** Подсказки шага от самой «глубокой» (окно уже открыто) к самой общей (открой вкладку). */
export function tutorialHints(
  step: number,
  castle: CastleView,
  path: string,
  marching = false,
  city3d = false,
): (Hint & { when?: boolean })[] {
  const fields = path.startsWith('/game/fields');
  const city = path === '/game' || path === '/game/';
  const map = path.startsWith('/game/map');
  const builds = (type: string) => building(castle, type)?.upgradingTo != null;
  switch (step) {
    case 1:
      return [
        { tut: 'build-farm', text: 'buildFarm', dim: true },
        { text: 'waitBuild', wait: true, when: builds('farm') },
        { text: 'tapFarm', when: fields || (city3d && city) },
        // 3D-город: ферма в той же сцене, вкладки «Поля» нет — ведём в город
        city3d ? { tut: 'nav-city', text: 'openCastle', dim: true } : { tut: 'nav-fields', text: 'openFields', dim: true },
      ];
    case 2:
      return [
        { tut: 'build-barracks', text: 'buildBarracks', dim: true },
        { text: 'waitBuild', wait: true, when: builds('barracks') },
        { text: 'tapBarracks', when: city },
        { tut: 'nav-city', text: 'openCastle', dim: true },
      ];
    case 3:
      return [
        // Найм идёт — ждём, иначе подсказка просила бы нанять ещё раз
        { tut: 'unit-training', text: 'waitTrain', wait: true },
        { tut: 'train-form', text: 'train', dim: true },
        { tut: 'unit-infantry_1', text: 'pickUnit', dim: true },
        { text: 'tapBarracksTrain', when: city },
        { tut: 'nav-city', text: 'openCastle', dim: true },
      ];
    case 4:
    case 5: {
      // Войска уже идут — ждём боя; вернулись без победы — отправить снова
      if (step === 5 && marching) return [{ text: 'battle', wait: true }];
      const hints: (Hint & { when?: boolean })[] = [
        { tut: 'march-send', text: 'send', dim: true },
        { tut: 'march-all', text: 'all', dim: true },
        { tut: 'map-attack', text: 'attack', dim: true },
        { tut: 'map-tutorial-target', text: 'findCamp', dim: true },
        { text: 'mapLoading', when: map },
        { tut: 'nav-map', text: step === 5 ? 'retry' : 'openMap', dim: true },
      ];
      // Сразу после отправки список походов ещё не обновился — не показываем «Все» и «Атаковать» снова
      return step === 5 ? [{ text: 'battle', wait: true, when: map }, ...hints.slice(2)] : hints;
    }
    case 6:
      return [
        { tut: 'hero-create-form', text: 'heroForm', dim: true },
        { tut: 'hero-create', text: 'heroCreate', dim: true },
        { tut: 'menu-heroes', text: 'menuHeroes', dim: true },
        { tut: 'nav-more', text: 'openMenu', dim: true },
      ];
    default:
      return [];
  }
}

function visibleTarget(tut: string): HTMLElement | null {
  for (const el of document.querySelectorAll<HTMLElement>(`[data-tut="${tut}"]`)) {
    const r = el.getBoundingClientRect();
    if (r.width > 0 && r.height > 0 && getComputedStyle(el).visibility !== 'hidden') return el;
  }
  return null;
}

/** Открытое окно закрывает нижнее меню и всё под собой: вернёт его кнопку закрытия, если цель не внутри окна. */
function closeButtonOver(target: HTMLElement): HTMLElement | null {
  const dialog = [...document.querySelectorAll<HTMLElement>('[role="dialog"][aria-modal="true"]')].pop();
  if (!dialog || dialog.contains(target)) return null;
  return dialog.querySelector<HTMLElement>('[data-tut="window-close"]');
}

type Box = { x: number; y: number; w: number; h: number };

export function TutorialGuide() {
  const { t } = useTranslation();
  const castle = useGame((s) => s.castle);
  const marching = useGame((s) => s.marches.some((m) => m.status === 'moving' || m.status === 'returning'));
  const { pathname } = useLocation();
  const [hint, setHint] = useState<Hint | null>(null);
  const [box, setBox] = useState<Box | null>(null);
  const [hiddenStep, setHiddenStep] = useState<number | null>(null);
  const [shake, setShake] = useState(0);
  const scrolled = useRef<string | null>(null);
  const step = castle && !castle.user.tutorialDone ? castle.user.tutorialStep : 0;
  const city3d = useHasCity3D(castle?.castle.race);
  const hidden = hiddenStep === step;

  // Каждые 200 мс: какая подсказка сейчас уместна и где её кнопка
  useEffect(() => {
    if (!castle || !step || hidden) {
      setHint(null);
      return;
    }
    const tick = () => {
      const list = tutorialHints(step, castle, pathname, marching, city3d);
      for (const h of list) {
        if (h.when === false) continue;
        if (h.tut) {
          const found = visibleTarget(h.tut);
          if (!found) continue;
          const close = closeButtonOver(found);
          // Ожидание под открытым окном: только текст, закрывать окно ради него незачем
          if (close && h.wait) {
            setHint(h);
            setBox(null);
            return;
          }
          const el = close ?? found;
          const key = close ? 'window-close' : h.tut;
          if (scrolled.current !== key) {
            scrolled.current = key;
            const r = el.getBoundingClientRect();
            if (r.top < 60 || r.bottom > window.innerHeight - 80) el.scrollIntoView({ block: 'center' });
          }
          const r = el.getBoundingClientRect();
          setHint(close ? { tut: 'window-close', text: 'closeWindow', dim: true } : h);
          setBox({ x: r.left, y: r.top, w: r.width, h: r.height });
          return;
        }
        if (h.when === undefined && !h.wait) continue;
        setHint(h);
        setBox(null);
        return;
      }
      setHint(null);
      setBox(null);
    };
    tick();
    const id = window.setInterval(tick, 200);
    return () => window.clearInterval(id);
  }, [castle, step, pathname, hidden, marching]);

  // Состояние обновляется само (чаще — пока идёт стройка, найм или бой): шаг сменится без нажатий
  useEffect(() => {
    if (!step) return;
    const id = window.setInterval(() => void reloadCastle().catch(() => undefined), hint?.wait ? 4000 : 8000);
    return () => window.clearInterval(id);
  }, [step, hint?.wait]);

  if (!castle || !step) return null;
  if (hidden)
    return createPortal(
      <button
        type="button"
        className="fixed bottom-[calc(84px+env(safe-area-inset-bottom))] left-2 z-[55] rounded-full border-2 border-gold-400 bg-ink-900 px-3 py-2 text-sm font-semibold text-gold-300 shadow-lg"
        onClick={() => setHiddenStep(null)}
      >
        🎓 {t('game:guide.show')}
      </button>,
      document.body,
    );
  if (!hint) return null;

  const skip = async () => {
    if (!window.confirm(t('game:guide.skipConfirm'))) return;
    await post('/tutorial/skip');
    await reloadCastle();
  };
  const pad = 6;
  const ring = box ? { x: box.x - pad, y: box.y - pad, w: box.w + pad * 2, h: box.h + pad * 2 } : null;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  // Пузырь — под кнопкой, если там есть место, иначе над ней; без кнопки — внизу над меню (вверху
  // экрана замка и полей — нужные участки, их не закрываем)
  const below = ring ? ring.y + ring.h + 12 : 64;
  const placeBelow = !ring || below + 150 < vh;
  // Рука над кнопкой, если под ней нет места (кнопка у нижнего края экрана); пузырь тогда выше руки
  const handAbove = !ring || placeBelow || ring.y + ring.h + HAND_SIZE > vh;
  const bubbleStyle = ring
    ? placeBelow
      ? { top: below }
      : { bottom: vh - ring.y + (handAbove ? HAND_SIZE + 8 : 12) }
    : { bottom: 'calc(84px + env(safe-area-inset-bottom))' };
  const nudge = () => setShake((n) => n + 1);

  return createPortal(
    <div className="pointer-events-none fixed inset-0 z-[55]" aria-live="polite">
      {ring && hint.dim && (
        <>
          {/* Затемнение вокруг кнопки: нажатия мимо не проходят, подсказка «вздрагивает» */}
          <div
            className="pointer-events-auto absolute inset-x-0 top-0 bg-black/60"
            style={{ height: Math.max(0, ring.y) }}
            onClick={nudge}
          />
          <div
            className="pointer-events-auto absolute inset-x-0 bottom-0 bg-black/60"
            style={{ top: ring.y + ring.h }}
            onClick={nudge}
          />
          <div
            className="pointer-events-auto absolute left-0 bg-black/60"
            style={{ top: ring.y, height: ring.h, width: Math.max(0, ring.x) }}
            onClick={nudge}
          />
          <div
            className="pointer-events-auto absolute right-0 bg-black/60"
            style={{ top: ring.y, height: ring.h, left: ring.x + ring.w }}
            onClick={nudge}
          />
        </>
      )}
      {ring && (
        <>
          <div
            className="absolute animate-pulse rounded-xl border-4 border-gold-300 shadow-[0_0_24px_rgba(255,214,90,.8)]"
            style={{ left: ring.x, top: ring.y, width: ring.w, height: ring.h }}
          />
          <div
            className="absolute animate-bounce text-4xl drop-shadow-[0_2px_4px_rgba(0,0,0,.8)]"
            style={{
              left: Math.min(vw - 44, Math.max(4, ring.x + ring.w / 2 - 18)),
              top: handAbove ? ring.y - HAND_SIZE : ring.y + ring.h + 2,
            }}
          >
            {handAbove ? '👇' : '👆'}
          </div>
        </>
      )}
      <div
        key={shake}
        className={cx(
          'pointer-events-auto absolute inset-x-3 mx-auto max-w-sm rounded-2xl border-2 border-gold-400 bg-ink-900/95 p-3 shadow-2xl',
          shake > 0 && 'animate-[sk-shake_.35s_ease-in-out]',
        )}
        style={bubbleStyle}
      >
        <div className="mb-1 flex items-center justify-between text-xs text-gold-300">
          <b>🎓 {t('game:guide.title', { n: step, total: TOTAL })}</b>
          <button type="button" className="px-1 text-parchment-300" onClick={() => setHiddenStep(step)}>
            {t('game:guide.hide')}
          </button>
        </div>
        <p className="text-[15px] leading-snug">{t(`game:guide.${hint.text}`)}</p>
        <button
          type="button"
          className="mt-2 text-xs text-parchment-300 underline"
          onClick={() => void skip()}
        >
          {t('game:guide.skip')}
        </button>
      </div>
    </div>,
    document.body,
  );
}
